import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OAuthProvider } from "@nestcoinco/hyper-api-gateway-api-client";

import "./LoggingInPage.scss";
import ImageGoogleSmallButtonIcon from "../../../images/google.svg";
import DefaultOnboardingStatusPage from "../../ReusablePages/DefaultOnboardingStatusPage/DefaultOnboardingStatusPage";
import { handleVerifyAuth } from "../../../api-clients/usersAuthClient";
import {
  getStoredAuthFlow,
  getStoredAuthProvider,
  setStoredAuthCancelledError,
} from "../../../utils/storage";
import FacebookLogo from "../../../images/facebook-logo.svg";
import { AuthFlow, OnboardingSteps, ROUTES } from "../../../utils/constants";
import GameControllerIcon from "../../../components/icons/GameControllerIcon";
import LoadingDots from "../../../components/LoadingDots/LoadingDots";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import { useSetAtom } from "jotai";
import { userAtom } from "../../../jotai/atoms/user";
import useAnalytics from "../../../hooks/useAnalytics";
import { SIGNUP_METHODS, SignupOptionProperty } from "../../../utils/analytics";
import useSavePhoenixClickMutation from "../../../react-query/mutations/use-save-phoenix-click";
import { getPhoenixClickId } from "../../../utils/phoenix-click";

const fallbackPath = `${ROUTES.ROOT.LOG_IN}`;

function LoggingInPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const setUser = useSetAtom(userAtom);
  const queryParams = new URLSearchParams(location.search);
  const [token, setToken] = useState<string>();
  const base64Data = queryParams.get("data");
  const base64Error = queryParams.get("error");
  const authFlow = getStoredAuthFlow();
  const authFlowText =
    authFlow === AuthFlow.LOGIN ? "Logging in" : "Signing up";
  const titleText = (
    <span className="d-flex">
      {`${authFlowText} with ${
        getStoredAuthProvider() === OAuthProvider.FACEBOOK
          ? "Facebook"
          : getStoredAuthProvider() === OAuthProvider.GOOGLE
          ? "Google"
          : ""
      } `}{" "}
      <LoadingDots dotStyles={{ background: "white" }} />
    </span>
  );
  const { goToStep } = useOnboardingSteps();
  const {
    sendSuccessfulSignupEvent,
    sendFailedSignupEvent,
    sendSuccessfulSigninEvent,
  } = useAnalytics();
  const { mutate: savePhoenixClick } = useSavePhoenixClickMutation();

  // Handle error from OAuth redirect
  useEffect(() => {
    if (!base64Error) return;

    const authProvider = getStoredAuthProvider();
    if (authProvider) {
      sendFailedSignupEvent({
        signup_method: SIGNUP_METHODS[authProvider],
        signup_option:
          authFlow === AuthFlow.LOGIN
            ? SignupOptionProperty.LOG_IN
            : SignupOptionProperty.SIGN_UP,
      });
    }
    setStoredAuthCancelledError(JSON.parse(atob(base64Error)));
    navigate(fallbackPath);
  }, [authFlow, base64Error, navigate, sendFailedSignupEvent]);

  // Extract token from base64 data
  useEffect(() => {
    function getToken(data: string) {
      const { idToken } = JSON.parse(atob(data));
      idToken && setToken(idToken);
    }
    base64Data && getToken(base64Data);
  }, [base64Data]);

  // Verify auth token and navigate accordingly
  useEffect(() => {
    const authProvider = getStoredAuthProvider();
    if (!authProvider) {
      navigate(fallbackPath);
      return;
    }
    if (!token) return;

    const verifyAuth = async () => {
      const signupMethod = SIGNUP_METHODS[authProvider];
      const userInfo = await handleVerifyAuth(token, authProvider);
      if (!userInfo) {
        sendFailedSignupEvent({
          signup_method: signupMethod,
          signup_option:
            authFlow === AuthFlow.LOGIN
              ? SignupOptionProperty.LOG_IN
              : SignupOptionProperty.SIGN_UP,
        });
        navigate(fallbackPath);
        return;
      }

      setUser(userInfo);
      const isReturningUser = userInfo.isReturning;
      const { id: userid, email, username } = userInfo.user;
      let redirectPath;

      const phxClickId = getPhoenixClickId();
      if (phxClickId) {
        savePhoenixClick(phxClickId);
      }

      if (isReturningUser) {
        sendSuccessfulSigninEvent(signupMethod, { userid, email, username });
        goToStep(OnboardingSteps.LOG_IN_SUCCESSFUL);
        redirectPath = `${ROUTES.ROOT.LOG_IN}/${ROUTES.LOG_IN.WELCOME}`;
      } else {
        sendSuccessfulSignupEvent(signupMethod, { userid, email, username });
        goToStep(OnboardingSteps.SIGN_UP_SUCCESSFUL);
        redirectPath = ROUTES.ROOT.SIGN_UP;
      }

      navigate(redirectPath);
    };
    verifyAuth();
  }, [
    token,
    authFlow,
    goToStep,
    navigate,
    setUser,
    sendFailedSignupEvent,
    sendSuccessfulSignupEvent,
    sendSuccessfulSigninEvent,
    savePhoenixClick,
  ]);

  return (
    <div>
      <DefaultOnboardingStatusPage
        titleText={titleText}
        secondaryText="Please wait a moment while we create your account"
        primaryImage={
          getStoredAuthProvider() === OAuthProvider.FACEBOOK
            ? FacebookLogo
            : ImageGoogleSmallButtonIcon
        }
        secondaryIcon={
          <GameControllerIcon className="spinning-controller" width={40} />
        }
        stayOnPage
      />
    </div>
  );
}

export default LoggingInPage;
